<template>
  <div id="app">
    <the-navbar
      v-show="showNavbar"
      @clickToggleRecentSearchBox="toggleRecentSearchBox"
      @clickShowBookmarks="showBookmarks"
      @clickSettings="showSettingsModal"
      @clickTitle="setPageType('search')"
      @clickAlbumName="getAlbumTracks"
      :pageType="pageType"
      :recentSearch="recentSearch"
      :bookmarkAlbums="bookmarkAlbums"
      :settings="settings"
      :isMobile="isMobile"
      :showRecentSearchBox="showRecentSearchBox"
    >
    </the-navbar>
    <the-searchbar
      @clickSearch="searchAlbums"
      @clickClearSearch="clearSearch"
      :recentSearch="recentSearch"
      :newSearchQuery="searchQuery"
      :settings="settings"
      :display-hero-title="displayHeroTitle === 'true'"
    >
    </the-searchbar>

    <main>
      <transition name="fade">
        <recent-search-box
          v-if="showRecentSearchBox && recentSearch.length > 0"
          :recentSearch="recentSearch"
          @clickSearchItem="searchAlbums"
          @clickRemoveRecentSearchItem="removeRecentSearchItem"
        >
        </recent-search-box>
      </transition>
      <album-list
        @clickUpdateSettings="updateSettings"
        @clickAlbumName="getAlbumTracks"
        :clickBookmarkAlbum="bookmarkAlbum"
        :isInBookmark="isInBookmark"
        :albums="pageType === 'search' ? albums : bookmarkAlbums"
        :pageType="pageType"
        :isAlbumLoading="isAlbumLoading"
        :searchFailed="searchFailed"
        :settings="settings"
        :isMobile="isMobile"
      >
      </album-list>
      <!-- settings modal -->
      <b-modal
        :active.sync="isSettingsModalActive"
        :canCancel="true"
        has-modal-card
      >
        <the-settings
          :settings="settings"
          @clickUpdateSettings="updateSettings"
        >
        </the-settings>
      </b-modal>
      <!-- album tracklist modal -->
      <b-modal
        :active.sync="isAlbumTracksModalActive"
        :canCancel="true"
        :has-modal-card="true"
        :onCancel="resetAlbumTracks"
        scroll="clip"
        :full-screen="isMobile"
      >
        <div
          class="columns is-mobile is-centered"
          v-if="!albumTracksFailed && albumTracks.length === 0"
        >
          <div class="columns is-mobile">
            <div class="column loading">
              <b-loading
                :is-full-page="false"
                :active.sync="isAlbumTracksLoading"
                :can-cancel="false"
              ></b-loading>
            </div>
          </div>
        </div>
        <div class="container" v-else-if="albumTracksFailed">
          <div class="columns is-mobile is-centered">
            <div class="column is-full">
              <b-message type="is-info" has-icon>
                No se pudieron cargar las ofertas. Es posible que el producto ya
                no esté en stock. Usa el buscador para intentarlo de nuevo
              </b-message>
            </div>
          </div>
        </div>
        <album-track-list
          v-else
          :albumTracks="albumTracks"
          :clickBookmarkAlbum="bookmarkAlbum"
          :isInBookmark="isInBookmark"
          :settings="settings"
          :isMobile="isMobile"
        >
        </album-track-list>
      </b-modal>
    </main>
  </div>
</template>

<script>
import TheNavbar from "./components/TheNavbar";
import TheSearchbar from "./components/TheSearchbar";
import RecentSearchBox from "./components/RecentSearchBox";
import AlbumList from "./components/AlbumList";
import TheSettings from "./components/TheSettings";
// import AlbumTrackList from "./components/AlbumTrackList";
import { mapGetters } from "vuex";
import { ToastProgrammatic as Toast } from "buefy";
import { DialogProgrammatic as Dialog } from "buefy";

export default {
  name: "app",
  data() {
    return {
      isSettingsModalActive: false,
      isAlbumTracksModalActive: false,
      windowWidth: window.innerWidth,
      showNavbar: true,
      apiBaseUrl: "https://ofertete-api.vercel.app"
    };
  },
  props: {
    displayHeroTitle: {
      type: [Boolean, String],
      required: false,
      default: true
    }
  },
  components: {
    TheNavbar,
    TheSearchbar,
    RecentSearchBox,
    TheSettings,
    AlbumList,
    AlbumTrackList: () =>
      import(/* webpackPrefetch: true */ "./components/AlbumTrackList")
  },
  computed: {
    ...mapGetters({
      recentSearch: "GET_RECENT_SEARCH",
      albums: "GET_ALBUMS",
      albumTracks: "GET_ALBUM_TRACKS",
      searchQuery: "SEARCH_QUERY",
      initialSearchQuery: "INITIAL_SEARCH_QUERY",
      bookmarkAlbums: "BOOKMARK_ALBUMS",
      pageType: "PAGE_TYPE",
      showRecentSearchBox: "SHOW_RECENT_SEARCH_BOX",
      isAlbumLoading: "IS_ALBUM_LOADING",
      isAlbumTracksLoading: "IS_ALBUM_TRACKS_LOADING",
      searchFailed: "SEARCH_FAILED",
      albumTracksFailed: "ALBUM_TRACKS_FAILED",
      settings: "SETTINGS",
      isAppError: "APP_ERROR"
    }),
    showRecentSearchBox() {
      return this.$store.state.showRecentSearchBox;
    },
    isMobile() {
      return this.$mq === "mobile";
    }
  },
  created() {
    this.$store.dispatch("GET_SETTINGS");
    this.$store.dispatch("GET_RECENT_SEARCH");
    this.$store.dispatch("GET_BOOKMARK_ALBUMS");
    // window.addEventListener("scroll", this.toggleNavbar);
  },
  destroyed() {
    window.removeEventListener("scroll", this.toggleNavbar);
  },
  methods: {
    searchAlbums(query) {
      if (query) {
        const payload = {
          url:
            `${this.apiBaseUrl}/api/search?q=${encodeURIComponent(query)}` +
            (this.settings.amazonOnly ? "&amazon=1" : ""),
          query
        };
        this.$store.dispatch("SEARCH_ALBUMS", payload);
      }
      this.$store.commit("SET_PAGE_TYPE", "search");
    },
    clearSearch() {
      this.$store.commit("CLEAR_SEARCH");
    },
    toggleRecentSearchBox() {
      this.$store.commit("TOGGLE_RECENT_SEARCH");
    },
    removeRecentSearchItem(item) {
      this.$store.dispatch("REMOVE_RECENT_SEARCH_ITEM", item);
    },
    bookmarkAlbum(album) {
      if (this.isInBookmark(album.name)) {
        Dialog.confirm({
          message: `¿Deseas eliminar el producto <b>${album.name}</b> de tu lista de favoritos?`,
          type: "is-danger",
          hasIcon: false,
          onConfirm: () => {
            this.$store.dispatch("BOOKMARK_ALBUM", {
              album: album,
              status: "unbookmarked"
            });
            Toast.open({
              duration: 3000,
              message: `<b>${album.name}</b> quitado de favoritos`,
              position: "is-bottom-right",
              type: "is-danger"
            });
          }
        });
      } else {
        Toast.open({
          duration: 3000,
          message: `${album.name} añadido a favoritos`,
          position: "is-bottom",
          type: "is-info"
        });
        this.$store.dispatch("BOOKMARK_ALBUM", {
          album: album,
          status: "bookmark"
        });
      }
    },
    isInBookmark(albumName) {
      return (
        this.bookmarkAlbums.findIndex(album => album.name === albumName) > -1
      );
    },
    showBookmarks() {
      this.$store.commit("SET_PAGE_TYPE", "bookmarks");
    },
    updateSettings(settingName, settingValue) {
      const payload = { settingName: settingName, settingValue: settingValue };
      this.$store.dispatch("UPDATE_SETTINGS", payload);
    },
    showSettingsModal() {
      this.isSettingsModalActive = true;
    },
    getAlbumTracks(album) {
      if (album) {
        this.isAlbumTracksModalActive = true;
        const payload = {
          url: `${this.apiBaseUrl}/api/offers?q=${album.normalised_name}`
        };

        if (
          (this.settings.amazonOnly && !this.isInBookmark(album.name)) ||
          (this.isInBookmark(album.name) && album.amazon_only)
        ) {
          payload.url = payload.url + "&amazon=1";
        }

        this.$store.dispatch("GET_ALBUM_TRACKS", payload);
      }
    },
    setPageType(pageType) {
      if (pageType !== this.pageType) {
        this.$store.commit("SET_PAGE_TYPE", pageType);
      }

      if (
        pageType === "search" &&
        this.initialSearchQuery !== this.searchQuery
      ) {
        this.searchAlbums(this.initialSearchQuery);
      }
    },
    resetAlbumTracks() {
      this.$store.commit("RESET_ALBUM_TRACKS");
    },
    toggleNavbar() {
      let scrollBarPosition = window.pageYOffset | document.body.scrollTop;
      if (scrollBarPosition > 100) {
        this.showNavbar = false;
      } else {
        this.showNavbar = true;
      }
    }
  }
};
</script>
<style lang="scss">
// Import Bulma's core
@import "~bulma/sass/utilities/_all";

$hero-body-padding: 1.5rem;
$section-padding: 0.5rem;
// Set your colors
$primary: #fd9760;
$primary-invert: findColorInvert($primary);

// Setup $colors to use as bulma classes (e.g. 'is-twitter')
$colors: (
  "white": (
    $white,
    $black
  ),
  "black": (
    $black,
    $white
  ),
  "light": (
    $light,
    $light-invert
  ),
  "dark": (
    $dark,
    $dark-invert
  ),
  "primary": (
    $primary,
    $primary-invert
  ),
  "info": (
    $info,
    $info-invert
  ),
  "success": (
    $success,
    $success-invert
  ),
  "warning": (
    $warning,
    $warning-invert
  ),
  "danger": (
    $danger,
    $danger-invert
  )
);

// Links
$link: $primary;
$link-invert: $primary-invert;
$link-focus-border: $primary;

/**
Este lio de importar librerias es porque si lo hago como se explica aquí https://buefy.org/documentation/customization, lo que va a ocurrir es que
se importa por defecto estilos que sobreescriben los de ofertete. Por ello, importo manualmente y dejo fuera lo que no necesite y que entre en conflicto
*/

// Import Bulma and Buefy styles
// @import "~bulma";

// @import "~bulma/sass/utilities/_all";
// @import "~bulma/sass/base/_all";

// @import "~bulma/sass/base/minireset.sass";
// @import "~bulma/sass/base/generic.sass";
@import "@/assets/sass/generic.sass";
@import "~bulma/sass/base/helpers.sass";

@import "~bulma/sass/elements/_all";
@import "~bulma/sass/form/_all";
@import "~bulma/sass/components/_all";
@import "~bulma/sass/grid/_all";
@import "~bulma/sass/layout/_all";

@import "~buefy/src/scss/buefy";

// transitions
.list-enter-active {
  transition: all 0.5s;
}
.list-leave-active {
  transition: all 0.5s;
}
.list-enter,
.list-leave-to {
  opacity: 0;
  transform: translateY(20px);
}
.fade-enter-active,
.component-fade-leave-active {
  transition: opacity 1.5s ease;
}
.fade-enter,
.component-fade-leave-to {
  opacity: 0;
}

.modal-card-title {
  flex-shrink: unset;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
}

.modal-close {
  background-color: rgba(10, 10, 10, 0.2);
}

.loading {
  padding-top: 50px;
  height: 300px;
}
.card-content {
  padding: 1rem !important;
}
.media-content {
  width: 180px !important;
}
.overflow-content div {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;
}
.media-wrap {
  border: 2px solid #f5f5f5;
}
.album-name {
  font-size: 1.05rem;
}
i {
  cursor: pointer;
}
.fa-heart::before {
  content: "❤";
}
.fa-star::before {
  content: "⭐";
}

.mdi-magnify::before {
  content: "🔎";
  // transform: rotate(-45deg);
  display: inline-block;
  font-style: normal;
}
.mdi-chevron-right::before {
  content: "➜";
}
.mdi-chevron-left::before {
  content: "➜";
  transform: rotate(180deg);
  display: inline-block;
}
.bookmarkIcon:not(.favorite) {
  color: transparent;
  text-shadow: 0 0 0 #ff15153d;
}
.bookmarkIcon:hover {
  color: rgb(255, 21, 21);
}
.favorite {
  color: rgb(255, 21, 21);
}

/**
html,
body,
dl,
dt,
dd,
blockquote,
figure,
fieldset,
legend,
textarea,
pre,
iframe,
hr
*/
ul,
li,
figure {
  margin: 0;
  padding: 0;
}

ul {
  list-style: none;
}
button,
input,
select,
textarea {
  margin: 0;
}
html {
  box-sizing: border-box;
}
*,
*::before,
*::after {
  box-sizing: inherit;
}
// img,
// video {
//   height: auto;
//   max-width: 100%;
// }
// iframe {
//   border: 0;
// }
// table {
//   border-collapse: collapse;
//   border-spacing: 0;
// }
// td,
// th {
//   padding: 0;
// }
// td:not([align]),
// th:not([align]) {
//   text-align: left;
// }
.emoji-color {
  color: transparent;
  text-shadow: 0 0 0 black;
}
</style>
